import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUmbralesMap } from '@/hooks/useUmbrales';
import { nombrePlantaDisplay } from '@/lib/plantas';
import { cn } from '@/lib/utils';
import { type BatchDetallePlanta, type UmbralAlerta, aplicarUmbral } from '@adelante/shared';
import { AlertTriangle, Columns3, GripVertical, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

/**
 * Tabla de batches con datos crudos de planta + columnas configurables.
 *
 * Features:
 *  - Catálogo de columnas agrupadas (Agua, Humedad, Cemento, Áridos, etc.).
 *  - Mostrar/ocultar columnas vía popover con checkboxes; persiste en
 *    localStorage para que el usuario no reconfigure cada vez.
 *  - Auto-oculta columnas 100% vacías en el dataset actual (los CSV traen
 *    muchas columnas sin datos según la planta).
 *  - Resaltado de valores fuera de rango (exceso de agua, humedad alta,
 *    desviación de dosis) para detectar el batch problemático de un vistazo.
 */

type Formato = 'num0' | 'num1' | 'num2' | 'num3' | 'pct' | 'bool' | 'fecha' | 'texto' | 'int';

interface ColumnaDef {
  key: string;
  label: string;
  grupo: string;
  get: (b: BatchDetallePlanta) => number | string | boolean | null;
  formato: Formato;
  /**
   * Clave del umbral parametrizable en `hor.umbrales_alerta`. Si no hay
   * config en BD (o aún no cargó), cae al `alertaDefault` de abajo.
   */
  claveUmbral?: string;
  /** Fallback hardcodeado por si la config aún no está disponible. */
  alertaDefault?: (v: number) => boolean;
  /** Visible por default (las más relevantes para detectar problemas). */
  defaultVisible: boolean;
  alineacion?: 'left' | 'right';
}

/**
 * Evalúa si una celda debe resaltarse como alerta: primero busca en el
 * catálogo parametrizado (Admin/Ingeniería puede ajustar los umbrales
 * desde /admin/umbrales); si no hay nada, usa el fallback hardcodeado.
 */
function alertaPorCelda(
  col: ColumnaDef,
  valor: number,
  umbralesMap: Map<string, UmbralAlerta>,
): boolean {
  if (col.claveUmbral) {
    const cfg = umbralesMap.get(col.claveUmbral);
    if (cfg) return aplicarUmbral(valor, cfg);
  }
  return col.alertaDefault?.(valor) ?? false;
}

const fmt = (n: number, dec: number) =>
  n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });

// Catálogo completo de columnas.
const COLUMNAS: ColumnaDef[] = [
  // Identificación
  {
    key: 'record_no',
    label: 'Record',
    grupo: 'Identificación',
    get: (b) => b.record_no,
    formato: 'int',
    defaultVisible: true,
    alineacion: 'right',
  },
  {
    key: 'fecha_inicio',
    label: 'Fecha',
    grupo: 'Identificación',
    get: (b) => b.fecha_inicio,
    formato: 'fecha',
    defaultVisible: true,
  },
  {
    key: 'planta_nombre',
    label: 'Planta',
    grupo: 'Identificación',
    get: (b) => b.planta_nombre,
    formato: 'texto',
    defaultVisible: true,
  },
  {
    key: 'codigo_interno_colada',
    label: 'Colada',
    grupo: 'Identificación',
    get: (b) => b.codigo_interno_colada,
    formato: 'int',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'recipe_name_raw',
    label: 'Receta',
    grupo: 'Identificación',
    get: (b) => b.recipe_name_raw,
    formato: 'texto',
    defaultVisible: true,
  },
  {
    key: 'cliente_raw',
    label: 'Destino',
    grupo: 'Identificación',
    get: (b) => b.cliente_raw,
    formato: 'texto',
    defaultVisible: false,
  },
  {
    key: 'm3_producidos',
    label: 'm³',
    grupo: 'Identificación',
    get: (b) => b.m3_producidos,
    formato: 'num2',
    defaultVisible: true,
    alineacion: 'right',
  },
  {
    key: 'operador',
    label: 'Operador',
    grupo: 'Identificación',
    get: (b) => b.operador,
    formato: 'texto',
    defaultVisible: false,
  },

  // Receta (diseño por 1 m³) — valores nominales de la receta.
  {
    key: 'arido_a_nombre',
    label: 'Árido A',
    grupo: 'Receta (1 m³)',
    get: (b) => b.arido_a_nombre,
    formato: 'texto',
    defaultVisible: false,
  },
  {
    key: 'arido_a_dosis_kg_m3',
    label: 'Árido A (kg/m³)',
    grupo: 'Receta (1 m³)',
    get: (b) => b.arido_a_dosis_kg_m3,
    formato: 'num0',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'arido_b_nombre',
    label: 'Árido B',
    grupo: 'Receta (1 m³)',
    get: (b) => b.arido_b_nombre,
    formato: 'texto',
    defaultVisible: false,
  },
  {
    key: 'arido_b_dosis_kg_m3',
    label: 'Árido B (kg/m³)',
    grupo: 'Receta (1 m³)',
    get: (b) => b.arido_b_dosis_kg_m3,
    formato: 'num0',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'cemento_dosis_kg_m3',
    label: 'Cemento (kg/m³)',
    grupo: 'Receta (1 m³)',
    get: (b) => b.cemento_dosis_kg_m3,
    formato: 'num0',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'agua_dosis_l_m3',
    label: 'Agua (L/m³)',
    grupo: 'Receta (1 m³)',
    get: (b) => b.agua_dosis_l_m3,
    formato: 'num0',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'aditivo1_dosis_l_m3',
    label: 'Adit.1 (L/m³)',
    grupo: 'Receta (1 m³)',
    get: (b) => b.aditivo1_dosis_l_m3,
    formato: 'num2',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'aditivo2_dosis_l_m3',
    label: 'Adit.2 (L/m³)',
    grupo: 'Receta (1 m³)',
    get: (b) => b.aditivo2_dosis_l_m3,
    formato: 'num2',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'aditivo3_dosis_l_m3',
    label: 'Adit.3 (L/m³)',
    grupo: 'Receta (1 m³)',
    get: (b) => b.aditivo3_dosis_l_m3,
    formato: 'num2',
    defaultVisible: false,
    alineacion: 'right',
  },

  // Agua — lo más crítico para resistencia
  {
    key: 'agua_l',
    label: 'Agua (L)',
    grupo: 'Agua',
    get: (b) => b.agua_l,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'agua_l_teor',
    label: 'Agua teór. (L)',
    grupo: 'Agua',
    get: (b) => b.agua_l_teor,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'agua_delta_l',
    label: 'Δ Agua (L)',
    grupo: 'Agua',
    get: (b) => b.agua_delta_l,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'agua_delta_pct',
    claveUmbral: 'batches.agua_delta_pct',
    label: 'Δ Agua %',
    grupo: 'Agua',
    get: (b) => b.agua_delta_pct,
    formato: 'pct',
    defaultVisible: true,
    alineacion: 'right',
    alertaDefault: (v) => Math.abs(v) >= 5,
  },
  {
    key: 'water_dosage_adj',
    label: 'Ajuste agua?',
    grupo: 'Agua',
    get: (b) => b.water_dosage_adj,
    formato: 'bool',
    defaultVisible: false,
  },
  {
    key: 'water_total_adj_l',
    claveUmbral: 'batches.water_total_adj_l',
    label: 'Corr. agua (L)',
    grupo: 'Agua',
    get: (b) => b.water_total_adj_l,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
    alertaDefault: (v) => Math.abs(v) > 0,
  },
  {
    key: 'relacion_agua_cemento',
    claveUmbral: 'batches.relacion_agua_cemento',
    label: 'a/c',
    grupo: 'Agua',
    get: (b) => b.relacion_agua_cemento,
    formato: 'num3',
    defaultVisible: true,
    alineacion: 'right',
    alertaDefault: (v) => v >= 0.6,
  },

  // Humedad
  {
    key: 'arido_a_moisture_pct',
    claveUmbral: 'batches.arido_a_moisture_pct',
    label: 'Hum. Árido A %',
    grupo: 'Humedad',
    get: (b) => b.arido_a_moisture_pct,
    formato: 'num2',
    defaultVisible: true,
    alineacion: 'right',
    alertaDefault: (v) => v >= 12,
  },
  {
    key: 'arido_b_moisture_pct',
    claveUmbral: 'batches.arido_b_moisture_pct',
    label: 'Hum. Árido B %',
    grupo: 'Humedad',
    get: (b) => b.arido_b_moisture_pct,
    formato: 'num2',
    defaultVisible: true,
    alineacion: 'right',
    alertaDefault: (v) => v >= 12,
  },

  // Cemento
  {
    key: 'cemento_kg',
    label: 'Cemento (kg)',
    grupo: 'Cemento',
    get: (b) => b.cemento_kg,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'cemento_kg_teor',
    label: 'Cemento teór.',
    grupo: 'Cemento',
    get: (b) => b.cemento_kg_teor,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'cemento_delta_pct',
    claveUmbral: 'batches.cemento_delta_pct',
    label: 'Δ Cemento %',
    grupo: 'Cemento',
    get: (b) => b.cemento_delta_pct,
    formato: 'pct',
    defaultVisible: true,
    alineacion: 'right',
    alertaDefault: (v) => Math.abs(v) >= 5,
  },

  // Áridos
  {
    key: 'arido_a_kg',
    label: 'Árido A (kg)',
    grupo: 'Áridos',
    get: (b) => b.arido_a_kg,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'arido_a_kg_teor',
    label: 'Árido A teór.',
    grupo: 'Áridos',
    get: (b) => b.arido_a_kg_teor,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'arido_a_delta_pct',
    claveUmbral: 'batches.arido_a_delta_pct',
    label: 'Δ Árido A %',
    grupo: 'Áridos',
    get: (b) => b.arido_a_delta_pct,
    formato: 'pct',
    defaultVisible: false,
    alineacion: 'right',
    alertaDefault: (v) => Math.abs(v) >= 5,
  },
  {
    key: 'arido_b_kg',
    label: 'Árido B (kg)',
    grupo: 'Áridos',
    get: (b) => b.arido_b_kg,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'arido_b_kg_teor',
    label: 'Árido B teór.',
    grupo: 'Áridos',
    get: (b) => b.arido_b_kg_teor,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'arido_b_delta_pct',
    claveUmbral: 'batches.arido_b_delta_pct',
    label: 'Δ Árido B %',
    grupo: 'Áridos',
    get: (b) => b.arido_b_delta_pct,
    formato: 'pct',
    defaultVisible: false,
    alineacion: 'right',
    alertaDefault: (v) => Math.abs(v) >= 5,
  },

  // Aditivos
  {
    key: 'aditivo1_l',
    label: 'Adit.1 (L)',
    grupo: 'Aditivos',
    get: (b) => b.aditivo1_l,
    formato: 'num2',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'aditivo1_l_teor',
    label: 'Adit.1 teór.',
    grupo: 'Aditivos',
    get: (b) => b.aditivo1_l_teor,
    formato: 'num2',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'aditivo1_delta_pct',
    claveUmbral: 'batches.aditivo1_delta_pct',
    label: 'Δ Adit.1 %',
    grupo: 'Aditivos',
    get: (b) => b.aditivo1_delta_pct,
    formato: 'pct',
    defaultVisible: true,
    alineacion: 'right',
    alertaDefault: (v) => Math.abs(v) >= 10,
  },
  {
    key: 'aditivo2_l',
    label: 'Adit.2 (L)',
    grupo: 'Aditivos',
    get: (b) => b.aditivo2_l,
    formato: 'num2',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'aditivo2_l_teor',
    label: 'Adit.2 teór.',
    grupo: 'Aditivos',
    get: (b) => b.aditivo2_l_teor,
    formato: 'num2',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'aditivo2_delta_pct',
    claveUmbral: 'batches.aditivo2_delta_pct',
    label: 'Δ Adit.2 %',
    grupo: 'Aditivos',
    get: (b) => b.aditivo2_delta_pct,
    formato: 'pct',
    defaultVisible: false,
    alineacion: 'right',
    alertaDefault: (v) => Math.abs(v) >= 10,
  },
  {
    key: 'aditivo3_l',
    label: 'Adit.3 (L)',
    grupo: 'Aditivos',
    get: (b) => b.aditivo3_l,
    formato: 'num2',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'aditivo3_l_teor',
    label: 'Adit.3 teór.',
    grupo: 'Aditivos',
    get: (b) => b.aditivo3_l_teor,
    formato: 'num2',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'aditivo3_delta_pct',
    claveUmbral: 'batches.aditivo3_delta_pct',
    label: 'Δ Adit.3 %',
    grupo: 'Aditivos',
    get: (b) => b.aditivo3_delta_pct,
    formato: 'pct',
    defaultVisible: false,
    alineacion: 'right',
    alertaDefault: (v) => Math.abs(v) >= 10,
  },

  // Ambiente + flags
  {
    key: 'temp_ambiente_inicio',
    label: 'T° inicio',
    grupo: 'Ambiente',
    get: (b) => b.temp_ambiente_inicio,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'temp_ambiente_fin',
    label: 'T° fin',
    grupo: 'Ambiente',
    get: (b) => b.temp_ambiente_fin,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'production_rate',
    label: 'Prod. (m³/h)',
    grupo: 'Ambiente',
    get: (b) => b.production_rate,
    formato: 'num1',
    defaultVisible: false,
    alineacion: 'right',
  },
  {
    key: 'receta_modificada',
    label: 'Receta mod.?',
    grupo: 'Ambiente',
    get: (b) => b.receta_modificada,
    formato: 'bool',
    defaultVisible: true,
  },
  {
    key: 'cantidad_alarmas',
    claveUmbral: 'batches.cantidad_alarmas',
    label: 'Alarmas',
    grupo: 'Ambiente',
    get: (b) => b.cantidad_alarmas,
    formato: 'int',
    defaultVisible: true,
    alineacion: 'right',
    alertaDefault: (v) => v > 0,
  },
];

const STORAGE_KEY = 'batches-columnas-visibles';
const STORAGE_KEY_ORDEN = 'batches-columnas-orden';

function cargarVisiblesGuardadas(): Set<string> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr.filter((x): x is string => typeof x === 'string'));
  } catch {
    // ignore
  }
  return null;
}

const ORDEN_DEFAULT = COLUMNAS.map((c) => c.key);

/**
 * Orden persistido de columnas (izquierda→derecha). Se mergea con el
 * catálogo: claves nuevas del catálogo que no estén en lo guardado se
 * agregan al final, y claves obsoletas se descartan. Así, agregar columnas
 * nuevas en el código no rompe el orden guardado del usuario.
 */
function cargarOrdenGuardado(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ORDEN);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        const guardado = arr.filter((x): x is string => typeof x === 'string');
        const validas = guardado.filter((k) => ORDEN_DEFAULT.includes(k));
        const faltantes = ORDEN_DEFAULT.filter((k) => !validas.includes(k));
        return [...validas, ...faltantes];
      }
    }
  } catch {
    // ignore
  }
  return [...ORDEN_DEFAULT];
}

function formatear(valor: number | string | boolean | null, formato: Formato): string {
  if (valor === null || valor === undefined) return '—';
  switch (formato) {
    case 'int':
      return String(valor);
    case 'num0':
      return fmt(Number(valor), 0);
    case 'num1':
      return fmt(Number(valor), 1);
    case 'num2':
      return fmt(Number(valor), 2);
    case 'num3':
      return fmt(Number(valor), 3);
    case 'pct':
      return `${fmt(Number(valor), 1)}%`;
    case 'bool':
      return valor ? 'Sí' : 'No';
    case 'fecha':
      return String(valor).slice(0, 10);
    default:
      return String(valor);
  }
}

interface Props {
  batches: BatchDetallePlanta[];
  cargando?: boolean;
}

export function TablaBatchesDetalle({ batches, cargando = false }: Props) {
  // Catálogo de umbrales parametrizables; `alertaPorCelda` cae al fallback
  // hardcodeado si una clave no está en el map (cache aún cargando o claves
  // nuevas no seeded).
  const umbralesMap = useUmbralesMap();

  // Columnas que tienen al menos un valor no-nulo en el dataset actual.
  const columnasConDatos = useMemo(() => {
    const conDatos = new Set<string>();
    for (const col of COLUMNAS) {
      const tiene = batches.some((b) => {
        const v = col.get(b);
        return v !== null && v !== undefined && v !== '';
      });
      if (tiene) conDatos.add(col.key);
    }
    return conDatos;
  }, [batches]);

  // Columnas disponibles = catálogo ∩ con-datos. Las vacías se esconden auto.
  const columnasDisponibles = useMemo(
    () => COLUMNAS.filter((c) => columnasConDatos.has(c.key)),
    [columnasConDatos],
  );

  const [visibles, setVisibles] = useState<Set<string>>(() => {
    const guardadas = cargarVisiblesGuardadas();
    if (guardadas) return guardadas;
    return new Set(COLUMNAS.filter((c) => c.defaultVisible).map((c) => c.key));
  });

  // Orden izquierda→derecha de columnas (persistido + reordenable por drag).
  const [orden, setOrden] = useState<string[]>(() => cargarOrdenGuardado());
  const [arrastrando, setArrastrando] = useState<string | null>(null);

  // Persistir cambios.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(visibles)));
    } catch {
      // ignore
    }
  }, [visibles]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_ORDEN, JSON.stringify(orden));
    } catch {
      // ignore
    }
  }, [orden]);

  // Columnas a renderizar: disponibles ∩ visibles, ordenadas por `orden`.
  const columnasVisibles = useMemo(() => {
    const porKey = new Map(columnasDisponibles.map((c) => [c.key, c]));
    return orden
      .map((k) => porKey.get(k))
      .filter((c): c is ColumnaDef => c !== undefined && visibles.has(c.key));
  }, [columnasDisponibles, visibles, orden]);

  const toggle = (key: string) =>
    setVisibles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const resetDefault = () => {
    setVisibles(new Set(COLUMNAS.filter((c) => c.defaultVisible).map((c) => c.key)));
    setOrden([...ORDEN_DEFAULT]);
  };

  /** Reordena `orden`: mueve `keyArrastrada` justo antes de `keyDestino`. */
  const moverColumna = (keyArrastrada: string, keyDestino: string) => {
    if (keyArrastrada === keyDestino) return;
    setOrden((prev) => {
      const sin = prev.filter((k) => k !== keyArrastrada);
      const idx = sin.indexOf(keyDestino);
      if (idx === -1) return prev;
      sin.splice(idx, 0, keyArrastrada);
      return sin;
    });
  };

  // Agrupar columnas disponibles por su grupo para el popover.
  const grupos = useMemo(() => {
    const m = new Map<string, ColumnaDef[]>();
    for (const c of columnasDisponibles) {
      const arr = m.get(c.grupo) ?? [];
      arr.push(c);
      m.set(c.grupo, arr);
    }
    return Array.from(m.entries());
  }, [columnasDisponibles]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground">
          {batches.length} batch{batches.length === 1 ? '' : 'es'} · {columnasVisibles.length}{' '}
          columnas visibles · arrastrá los encabezados para reordenar
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5">
              <Columns3 className="h-4 w-4" /> Columnas
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="max-h-[60vh] w-72 overflow-y-auto">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium">Mostrar columnas</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={resetDefault}
              >
                <RotateCcw className="h-3 w-3" /> Default
              </Button>
            </div>
            <div className="space-y-3">
              {grupos.map(([grupo, cols]) => (
                <div key={grupo}>
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {grupo}
                  </div>
                  <div className="space-y-1">
                    {cols.map((c) => (
                      <label
                        key={c.key}
                        className="flex cursor-pointer items-center gap-2 text-sm hover:text-foreground"
                      >
                        <input
                          type="checkbox"
                          checked={visibles.has(c.key)}
                          onChange={() => toggle(c.key)}
                          className="h-3.5 w-3.5"
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columnasVisibles.map((c) => (
                <TableHead
                  key={c.key}
                  draggable
                  onDragStart={() => setArrastrando(c.key)}
                  onDragEnd={() => setArrastrando(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (arrastrando) moverColumna(arrastrando, c.key);
                    setArrastrando(null);
                  }}
                  title="Arrastrá para reordenar"
                  className={cn(
                    'cursor-move select-none whitespace-nowrap text-xs',
                    c.alineacion === 'right' && 'text-right',
                    arrastrando === c.key && 'opacity-40',
                    arrastrando !== null && arrastrando !== c.key && 'hover:bg-accent/60',
                  )}
                >
                  <span className="inline-flex items-center gap-1">
                    <GripVertical className="h-3 w-3 shrink-0 text-muted-foreground/40" />
                    {c.label}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {cargando && (
              <TableRow>
                <TableCell
                  colSpan={columnasVisibles.length || 1}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  Cargando…
                </TableCell>
              </TableRow>
            )}
            {!cargando && batches.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columnasVisibles.length || 1}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  No hay batches que coincidan.
                </TableCell>
              </TableRow>
            )}
            {!cargando &&
              batches.map((b) => (
                <TableRow key={b.id} className={cn(b.tuvo_alarma && 'bg-red-50/40')}>
                  {columnasVisibles.map((c) => {
                    const valor = c.get(b);
                    const esAlerta =
                      valor !== null &&
                      typeof valor === 'number' &&
                      alertaPorCelda(c, valor, umbralesMap);
                    return (
                      <TableCell
                        key={c.key}
                        className={cn(
                          'whitespace-nowrap text-xs',
                          c.alineacion === 'right' && 'text-right font-mono tabular-nums',
                          c.formato === 'int' && 'font-mono tabular-nums',
                          esAlerta && 'font-semibold text-red-700',
                        )}
                      >
                        <span className="inline-flex items-center gap-1">
                          {esAlerta && <AlertTriangle className="h-3 w-3" />}
                          {c.key === 'planta_nombre'
                            ? nombrePlantaDisplay(String(valor))
                            : formatear(valor, c.formato)}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
